import React from "react";
import { Box, Button, FormControlLabel, Switch, Typography } from "@mui/material";
import { useAuth } from "../../../common/auth";
import { C } from "../../../common/theme";
import { TrafficRecentView, getTrafficViews } from "../../../common/requests/platform/endpoints";
import { Column, DataTable, EmptyState, Section, StatusPill } from "../../../ui";
import { deviceLabel, errorMessage, formatWhen, useVisiblePolling } from "../platformData";
import ViewDetailDialog from "./ViewDetailDialog";

const PAGE_SIZE = 50;
/** New views are fetched this often while the period includes now */
const LIVE_MS = 5_000;
const LIVE_MAX = 100;

function who(row: TrafficRecentView) {
  if (row.username) return row.username;
  if (row.email) return row.email;
  if (row.user_id) return `user ${row.user_id.slice(0, 8)}`;
  return null;
}

type Cursor = { before: string; before_id: string } | null;

const cursorAfter = (rows: TrafficRecentView[]): Cursor => {
  const last = rows[rows.length - 1];
  // A full first page may have more behind it; a short one is the whole period.
  return rows.length >= PAGE_SIZE && last?.created_at && last.event_id ? { before: last.created_at, before_id: last.event_id } : null;
};

interface Props {
  /** The newest page, as returned with the traffic report */
  rows: TrafficRecentView[];
  /** The report's period, ISO [start, end) */
  start: string;
  end: string;
  /** The period runs past now: poll for new views every LIVE_MS */
  live: boolean;
}

interface Item {
  key: string;
  row: TrafficRecentView;
  fresh: boolean;
}

const ellipsis = { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 } as const;

export default function RecentViews({ rows: initialRows, start, end, live }: Props) {
  const { idToken } = useAuth();
  const tokenRef = React.useRef(idToken);
  tokenRef.current = idToken;

  const [hideAnon, setHideAnon] = React.useState(false);
  const [rows, setRows] = React.useState(initialRows);
  const [cursor, setCursor] = React.useState<Cursor>(() => cursorAfter(initialRows));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Bumped whenever the list is replaced, so a slow page for an old list is dropped.
  const generation = React.useRef(0);
  // The fixed-height box the rows scroll in.
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const sentinel = React.useRef<HTMLDivElement | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);
  // Rows that arrived by live update, marked for a moment.
  const [fresh, setFresh] = React.useState<Set<string>>(() => new Set());
  // Scroll height of the box before new rows were put on top, to keep the reader's place.
  const heightBefore = React.useRef<number | null>(null);
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;
  const hideAnonRef = React.useRef(hideAnon);
  hideAnonRef.current = hideAnon;
  const loadingRef = React.useRef(loading);
  loadingRef.current = loading;

  const fetchPage = React.useCallback(
    async (from: Cursor, signedInOnly: boolean, replace: boolean) => {
      const token = tokenRef.current;
      if (!token) return;
      const gen = ++generation.current;
      setLoading(true);
      setError(null);
      try {
        const res = await getTrafficViews(token, {
          start,
          end,
          limit: PAGE_SIZE,
          signed_in_only: signedInOnly,
          ...(from ? { before: from.before, before_id: from.before_id } : {}),
        });
        if (gen !== generation.current) return;
        const page = res.data;
        setRows((prev) => (replace ? page.rows : [...prev, ...page.rows]));
        setCursor(page.next_before && page.next_before_id ? { before: page.next_before, before_id: page.next_before_id } : null);
      } catch (e: any) {
        if (gen !== generation.current) return;
        setError(errorMessage(e, "Couldn't load more page views"));
      } finally {
        if (gen === generation.current) setLoading(false);
      }
    },
    [start, end]
  );

  /* live: views newer than the top row, put on top */
  const pollNew = React.useCallback(
    async (signal: AbortSignal) => {
      const token = tokenRef.current;
      // While a page is loading the list is about to change; the next poll catches up.
      if (!token || loadingRef.current) return;
      const gen = generation.current;
      const top = rowsRef.current[0];
      try {
        const res = await getTrafficViews(
          token,
          {
            start,
            // The report's end was "now" when it loaded; leave room for what came since.
            end: new Date(Date.now() + 60_000).toISOString(),
            limit: LIVE_MAX,
            signed_in_only: hideAnonRef.current,
            ...(top?.created_at && top.event_id ? { after: top.created_at, after_id: top.event_id } : {}),
          },
          signal
        );
        // The list was replaced meanwhile (period, toggle, reload): this answer is for the old one.
        if (signal.aborted || gen !== generation.current || res.data.rows.length === 0) return;
        const page = res.data;
        // Only hold the reader's place when they've scrolled down inside the box.
        const el = scrollRef.current;
        heightBefore.current = el && el.scrollTop > 0 ? el.scrollHeight : null;
        if (page.next_before) {
          // More arrived than one poll returns: start the list over from the newest.
          setRows(page.rows);
          setCursor({ before: page.next_before, before_id: page.next_before_id as string });
        } else {
          setRows((prev) => {
            const seen = new Set(prev.map((r) => r.event_id));
            return [...page.rows.filter((r) => !seen.has(r.event_id)), ...prev];
          });
        }
        setFresh(new Set(page.rows.map((r) => r.event_id).filter((id): id is string => !!id)));
      } catch {
        // A missed poll is retried by the next one.
      }
    },
    [start]
  );
  useVisiblePolling(live && !!idToken, LIVE_MS, pollNew);

  // Keep the rows the reader is looking at in place when new ones land on top.
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && heightBefore.current !== null) el.scrollTop += el.scrollHeight - heightBefore.current;
    heightBefore.current = null;
  }, [rows]);

  React.useEffect(() => {
    if (fresh.size === 0) return;
    const t = window.setTimeout(() => setFresh(new Set()), 2_500);
    return () => window.clearTimeout(t);
  }, [fresh]);

  // The report was reloaded (new period or refresh): start over from its newest page.
  React.useEffect(() => {
    generation.current++;
    setHideAnon(false);
    setRows(initialRows);
    setCursor(cursorAfter(initialRows));
    setLoading(false);
    setError(null);
  }, [initialRows]);

  const toggleAnon = (checked: boolean) => {
    setHideAnon(checked);
    if (checked) {
      setRows([]);
      setCursor(null);
      fetchPage(null, true, true);
    } else {
      generation.current++;
      setLoading(false);
      setError(null);
      setRows(initialRows);
      setCursor(cursorAfter(initialRows));
    }
  };

  // Load the next page when the bottom of the list scrolls into view.
  const hasMore = cursor !== null;
  React.useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || loading || error || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) fetchPage(cursor, hideAnon, false);
      },
      // Watch the scrolling box itself, so the next page loads just before its bottom.
      { root: scrollRef.current, rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, error, cursor, hideAnon, fetchPage]);

  const items: Item[] = React.useMemo(
    () => rows.map((row, i) => ({ key: row.event_id ?? `i${i}`, row, fresh: !!row.event_id && fresh.has(row.event_id) })),
    [rows, fresh]
  );

  const columns: Column<Item>[] = [
    {
      key: "time",
      header: "Time",
      width: 110,
      render: ({ row, fresh }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>
          <Typography variant="body2" className="num" sx={{ color: C.ink2 }}>
            {formatWhen(row.created_at)}
          </Typography>
          {fresh && <StatusPill tone="success">New</StatusPill>}
        </Box>
      ),
    },
    {
      key: "path",
      header: "Page",
      render: ({ row }) => (
        <Box component="span" title={row.path} sx={{ ...ellipsis, maxWidth: { md: 380, lg: 520 }, fontWeight: 600 }}>
          {row.path}
        </Box>
      ),
    },
    {
      key: "user",
      header: "User",
      render: ({ row }) => {
        const user = who(row);
        return (
          <Box component="span" title={row.email ?? undefined} sx={{ ...ellipsis, maxWidth: 220, color: user ? C.ink : C.muted }}>
            {user ?? "Anonymous"}
          </Box>
        );
      },
    },
    { key: "country", header: "Country", hideBelow: 1000, render: ({ row }) => row.country || "Unknown" },
    { key: "device", header: "Device", hideBelow: 1000, render: ({ row }) => deviceLabel(row.device_type) },
  ];

  const renderCard = ({ row, fresh }: Item) => {
    const user = who(row);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Box component="span" title={row.path} sx={{ ...ellipsis, flex: 1, fontWeight: 700, fontSize: 14 }}>
            {row.path}
          </Box>
          {fresh && <StatusPill tone="success">New</StatusPill>}
          <Typography variant="caption" className="num" sx={{ color: C.muted, whiteSpace: "nowrap" }}>
            {formatWhen(row.created_at)}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ ...ellipsis, color: C.muted }}>
          <Box component="span" sx={{ color: user ? C.ink2 : C.muted, fontWeight: user ? 600 : 400 }}>
            {user ?? "Anonymous"}
          </Box>
          {" · "}
          {row.country || "Unknown country"} · {deviceLabel(row.device_type)}
        </Typography>
      </Box>
    );
  };

  const footer = (
    <Box ref={sentinel} sx={{ minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, px: 1.5, py: 1 }}>
      {loading ? (
        <Typography variant="caption" sx={{ color: C.muted }} aria-live="polite">
          Loading more page views…
        </Typography>
      ) : error ? (
        <>
          <Typography variant="caption" sx={{ color: C.danger }}>
            {error}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => fetchPage(cursor, hideAnon, rows.length === 0)}>
            Retry
          </Button>
        </>
      ) : hasMore ? (
        <Button variant="outlined" size="small" onClick={() => fetchPage(cursor, hideAnon, false)} sx={{ minHeight: 40 }}>
          Load more
        </Button>
      ) : (
        <Typography variant="caption" sx={{ color: C.muted }}>
          {rows.length} page views, end of period
        </Typography>
      )}
    </Box>
  );

  return (
    <Section
      title="Latest page views"
      subtitle={
        live ? (
          <Box component="span" title="New page views appear every 5 seconds while this tab is open" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: C.leaf,
                animation: "livePulse 2s ease-in-out infinite",
                "@keyframes livePulse": { "50%": { opacity: 0.35 } },
              }}
            />
            Live · tap a row for details
          </Box>
        ) : (
          "Newest first · tap a row for details"
        )
      }
      actions={
        <FormControlLabel
          sx={{ mr: 0, minHeight: 44 }}
          control={<Switch checked={hideAnon} onChange={(e) => toggleAnon(e.target.checked)} />}
          label={<Typography variant="body2">Hide anonymous</Typography>}
        />
      }
    >
      <Box>
        <DataTable
          aria-label="Latest page views"
          rows={items}
          columns={columns}
          getRowId={(item) => item.key}
          loading={loading && rows.length === 0}
          error={rows.length === 0 ? error : null}
          onRetry={() => fetchPage(null, hideAnon, true)}
          empty={<EmptyState title={hideAnon ? "No signed-in page views in this period." : "No page views in this period."} />}
          onRowClick={(item) => item.row.event_id && setOpenId(item.row.event_id)}
          selectedId={openId}
          renderCard={renderCard}
          footer={rows.length > 0 ? footer : undefined}
          compact
          height={{ xs: 560, md: 480 }}
          hideScrollbar
          scrollRef={scrollRef}
        />
      </Box>
      <ViewDetailDialog eventId={openId} onClose={() => setOpenId(null)} />
    </Section>
  );
}
